import { ApiResponse, PaginationResponse } from '../../../../constants/types';
import {
  AssignmentClassItem,
  AssignmentRecitationItem,
  AssignmentStatusByClassItem,
  AssignmentStatusByStudentItem,
  AssignmentStatusDetailByClassItem,
  AssignmentStatusDetailByStudentItem,
  AssignmentStudentItem,
  ClassGroupSummaryItem,
  OrderByType,
  RecitationOrderByType,
  RecitationStatusType,
  RecitationStatusItem,
  RecitationStatusSummary,
  RecitationStatusBookItem,
  VoucherBalanceItem,
} from '../../../../constants/types/lms/recitation/type';
import {
  AssignmentStatusType,
  AssignmentType,
} from '../../../../constants/types/lms/type';
import instance from '../../../axios/utils.ts/instance';

interface AssignmentBookListParams {
  classId: string;
  type: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : 북 목록 조회
 * @param classId
 * @param type
 * @returns
 */
export const getAssignmentBookList = async (
  params: AssignmentBookListParams,
) => {
  const { classId, ...rest } = params;

  const response = await instance.get<ApiResponse<string[]>>(
    `/v1/lms/${classId}/todo/book-filter`,
    {
      params: rest,
    },
  );

  return response.data.data;
};

interface AssignmentLessonListParams {
  classId: string;
  type: string;
  book: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : 레슨 목록 조회
 * @param classId
 * @param type
 * @param book
 * @returns
 */
export const getAssignmentLessonList = async (
  params: AssignmentLessonListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<ApiResponse<string[]>>(
    `/v1/lms/${classId}/todo/lesson-filter`,
    {
      params: rest,
    },
  );

  return result.data.data;
};

interface AssignmentRecitationListParams {
  classId: string;
  type?: string;
  book?: string;
  lesson?: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : AI인증제 목록 조회
 * @param classId
 * @param type
 * @param book
 * @param lesson
 * @returns
 */
export const getAssignmentRecitationList = async (
  params: AssignmentRecitationListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentRecitationItem[]>>
  >(`/v1/lms/${classId}/todo`, { params: { page: 1, pageSize: 50, ...rest } });

  return result.data.data.result;
};

interface AssignmentClassListParams {
  classId: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : 반 목록 조회
 * @param classId
 * @returns
 */
export const getAssignmentClassList = async (
  params: AssignmentClassListParams,
) => {
  const { classId } = params;

  const result = await instance.get<ApiResponse<AssignmentClassItem[]>>(
    `/v1/lms/${classId}/todo/class-group-filter`,
  );

  return result.data.data;
};

interface AssignmentStudentListParams {
  classId: string;
  classGroupId?: number | number[];
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : 학생 목록 조회
 * @param classId
 * @param classGroupId
 * @returns
 */
export const getAssignmentStudentList = async (
  params: AssignmentStudentListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<ApiResponse<AssignmentStudentItem[]>>(
    `/v1/lms/${classId}/todo/student`,
    {
      params: rest,
    },
  );

  return result.data.data;
};

interface PostAssignmentsToStudentsParams {
  classId: string;
  toDoIds: number[];
  studentIds: number[];
  toDate: string;
  confirmDuplicates?: boolean;
}

/**
 * LMS > AI인증제 관리 > AI인증제 내주기 : AI인증제 배정
 * @param classId
 * @param toDoIds
 * @param studentIds
 * @param toDate
 * @param confirmDuplicates
 * @returns
 */
export const postAssignmentsToStudents = async (
  params: PostAssignmentsToStudentsParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.post(`/v1/lms/${classId}/todo/assign`, rest);

  return result.data;
};

/**
 * LMS > 과제 관리 > 과제 내주기 : 이용권 잔여 수량 조회
 * @returns
 */
export const getVoucherBalanceList = async () => {
  const result = await instance.get<
    ApiResponse<{ items: VoucherBalanceItem[] }>
  >(`/v1/lms/vouchers/balance`);

  return result.data.data;
};

/**
 * -----------------------------------------------------------------
 */

interface ClassGroupSummaryListParams {
  classId: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 반 목록 조회
 * @param classId
 * @returns
 */
export const getClassGroupSummaryList = async (
  params: ClassGroupSummaryListParams,
) => {
  const { classId } = params;

  const result = await instance.get<
    ApiResponse<{ groups: ClassGroupSummaryItem[] }>
  >(`/v1/lms/${classId}/todo/class-group-summary`, {
    params: { summaryType: 'RECITATION' },
  });

  return result.data.data;
};

interface AssignmentStatusListParams {
  classId: string;
  classGroupId?: number;
  unassignedGroup?: boolean;
  page?: number;
  pageSize?: number;
  toDoStatus?: AssignmentStatusType;
  toDoType?: AssignmentType;
  book?: string;
  lesson?: string;
  title?: string;
  uid?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  orderBy?: OrderByType;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 반별 AI인증제 현황 목록 조회
 * @param classId
 * @param classGroupId
 * @param unassignedGroup
 * @param page
 * @param pageSize
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusListByClass = async (
  params: AssignmentStatusListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusByClassItem[]>>
  >(`/v1/lms/${classId}/todo/state`, {
    params: { category: 'BY_TODO', ...rest },
  });

  return result.data.data;
};

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 학생별 AI인증제 현황 목록 조회
 * @param classId
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusListByStudent = async (
  params: AssignmentStatusListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusByStudentItem[]>>
  >(`/v1/lms/${classId}/todo/state`, {
    params: { category: 'BY_ID', ...rest },
  });

  return result.data.data;
};

interface AssignmentStatusDetailListByClassParams {
  classId: string;
  detailId: string;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 반별 AI인증제 현황 상세 조회
 * @param classId
 * @param detailId
 * @returns
 */
export const getAssignmentStatusDetailListByClass = async (
  params: AssignmentStatusDetailListByClassParams,
) => {
  const { classId, detailId } = params;

  const result = await instance.get<
    ApiResponse<AssignmentStatusDetailByClassItem[]>
  >(`/v1/lms/${classId}/todo/state/${detailId}`, {
    params: { category: 'BY_TODO' },
  });

  return result.data.data;
};

interface AssignmentStatusDetailListByStudentParams {
  classId: string;
  detailId: number;
  page: number;
  pageSize: number;
  toDoStatus?: AssignmentStatusType;
  toDoType?: AssignmentType;
  book?: string;
  lesson?: string;
  title?: string;
  uid?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  orderBy?: OrderByType;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 학생별 AI인증제 현황 상세 조회
 * @param classId
 * @param detailId
 * @param page
 * @param pageSize
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param fromDate
 * @param toDate
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusDetailListByStudent = async (
  params: AssignmentStatusDetailListByStudentParams,
) => {
  const { classId, detailId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusDetailByStudentItem[]>>
  >(`/v1/lms/${classId}/todo/state/${detailId}`, {
    params: { category: 'BY_ID', ...rest },
  });

  return result.data.data;
};

interface DeleteAssignmentParams {
  classId: string;
  txId: string;
  todoStatus: AssignmentStatusType;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : AI인증제 삭제
 * @param classId
 * @param txId
 * @param todoStatus
 * @returns
 */
export const deleteAssignment = async (params: DeleteAssignmentParams) => {
  const { classId, txId, todoStatus } = params;

  const result = await instance.delete(
    `/v1/lms/${classId}/todo/assign/${txId}?todoStatus=${todoStatus}`,
  );

  return result.data;
};

interface DeleteAssignmentByStudentParams {
  classId: string;
  todoId: number;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 학생 단위 AI인증제 삭제
 * @param classId
 * @param todoId
 * @returns
 */
export const deleteAssignmentByStudent = async (
  params: DeleteAssignmentByStudentParams,
) => {
  const { classId, todoId } = params;

  const result = await instance.delete(
    `/v1/lms/${classId}/todo/assigned/${todoId}`,
  );

  return result.data;
};

interface RecitationStatusListParams {
  classId: string;
  classGroupId?: number;
  unassignedGroup?: boolean;
  studentId?: number;
  result?: RecitationStatusType;
  orderBy?: RecitationOrderByType;
}

/**
 * LMS > AI인증제 관리 > AI인증제 현황 : 학생별 AI인증제 현황 상세 조회
 * @param classId
 * @param classGroupId
 * @param unassignedGroup
 * @param studentId
 * @param result // NOT_STARTED(미응시)/IN_PROGRESS(진행중)/ANALYZING(AI분석중)/PASSED(합격)/FAILED(불합격)/ANALYSIS_FAILED(분석실패)/INVALIDATED(무효 - 응시횟수 초과로 인증제 시스템이 무효 처리한 회차)
 * @param orderBy // ATTEMPTED_AT(기본, 응시일 최신순 - 미응시 행 마지막) / STUDENT_NAME(가나다) / LEVEL(오름차순)
 * @returns
 */
export const getRecitationStatusList = async (
  params: RecitationStatusListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<{
      books: RecitationStatusBookItem[];
      rows: RecitationStatusItem[];
      summary: RecitationStatusSummary;
    }>
  >(`/v1/lms/${classId}/jebs-ai-recitation/state`, {
    params: rest,
  });

  return result.data.data;
};
